import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupIcon from '@mui/icons-material/Group';
import PaidIcon from '@mui/icons-material/Paid';
import { Box, Card, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { ICustomerRefundMetricsProps, IMetricCardProps } from '../../types/customerRefundMetrics';

const MetricCard = ({ icon, iconColor, label, value, sub, tooltip }: IMetricCardProps) => (
  <Card
    variant="outlined"
    sx={{
      flex: 1,
      minWidth: 180,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    }}
  >
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: `${iconColor}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        textTransform="uppercase"
      >
        {label}
      </Typography>
    </Box>

    <Tooltip title={tooltip ?? ''} placement="bottom-start" disableHoverListener={!tooltip}>
      <Typography variant="h5" fontWeight={700} lineHeight={1}>
        {value}
      </Typography>
    </Tooltip>

    <Typography variant="caption" color="text.secondary">
      {sub}
    </Typography>
  </Card>
);

export const CustomerRefundMetrics = ({ data }: ICustomerRefundMetricsProps) => {
  const metrics = useMemo(() => {
    const total = data.length;
    const approved = data.filter((r) => r.status === 'APPROVED');
    const rejected = data.filter((r) => r.status === 'REJECTED');
    const resolved = approved.length + rejected.length;

    // Total amount refunded (approved only)
    const totalRefunded = approved.reduce((sum, r) => sum + Number(r.refund_amount), 0);

    // Success rate against all resolved requests
    const successRate = resolved > 0 ? (approved.length / resolved) * 100 : null;

    // Avg processing time for resolved requests (created_at → updated_at)
    const processingTimesMs =
      resolved > 0
        ? [...approved, ...rejected]
            .filter((r) => r.updated_at)
            .map((r) => new Date(r.updated_at!).getTime() - new Date(r.created_at).getTime())
        : [];

    const avgMs =
      processingTimesMs.length > 0
        ? processingTimesMs.reduce((a, b) => a + b, 0) / processingTimesMs.length
        : null;

    const formatAvgTime = (ms: number | null): { display: string; sub: string } => {
      if (ms === null) return { display: '—', sub: 'No resolved requests yet' };
      const totalMinutes = Math.round(ms / 60000);
      if (totalMinutes < 60) return { display: `${totalMinutes}m`, sub: 'average resolution time' };
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      if (hours < 24) {
        return {
          display: mins > 0 ? `${hours}h ${mins}m` : `${hours}h`,
          sub: 'average resolution time',
        };
      }
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return {
        display: remHours > 0 ? `${days}d ${remHours}h` : `${days}d`,
        sub: 'average resolution time',
      };
    };

    const avgTime = formatAvgTime(avgMs);

    return {
      total,
      pending: data.filter((r) => r.status === 'PENDING').length,
      totalRefunded,
      approved: approved.length,
      successRate,
      resolved,
      avgTime,
    };
  }, [data]);

  return (
    <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
      {/* Total Requests */}
      <MetricCard
        icon={<GroupIcon fontSize="small" />}
        iconColor="#1976d2"
        label="Total Requests"
        value={metrics.total.toLocaleString()}
        sub={`${metrics.pending} pending review`}
      />

      {/* Total Amount Refunded */}
      <MetricCard
        icon={<PaidIcon fontSize="small" />}
        iconColor="#2e7d32"
        label="Total Refunded"
        value={`₱${metrics.totalRefunded.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`}
        sub={`across ${metrics.approved} approved request${metrics.approved !== 1 ? 's' : ''}`}
      />

      {/* Success Rate */}
      <MetricCard
        icon={<CheckCircleOutlineIcon fontSize="small" />}
        iconColor="#ed6c02"
        label="Success Rate"
        value={metrics.successRate !== null ? `${metrics.successRate.toFixed(1)}%` : '—'}
        sub={
          metrics.resolved > 0
            ? `${metrics.approved} approved / ${metrics.resolved} resolved`
            : 'No resolved requests yet'
        }
        tooltip="Approved ÷ (Approved + Rejected)"
      />

      {/* Avg Processing Time */}
      <MetricCard
        icon={<AccessTimeIcon fontSize="small" />}
        iconColor="#9c27b0"
        label="Avg. Processing Time"
        value={metrics.avgTime.display}
        sub={metrics.avgTime.sub}
        tooltip="Average time from request submission to resolution (approved or rejected)"
      />
    </Box>
  );
};
